import { CustomisationsInterface, CustomCssVariables } from "@thoughtspot/visual-embed-sdk";
import { ThemeMode, themes } from "../theme/colors";

export const getCustomizations = (theme: ThemeMode): CustomisationsInterface => {
  const currentColors = themes[theme];

  return {
    style: {
      customCSS: {
        variables: {
          "--ts-var-root-background": "transparent",
          "--ts-var-root-color": currentColors.dark,
          "--ts-var-viz-background": currentColors.pureWhite,
          "--ts-var-viz-title-color": currentColors.pureBlack,
          "--ts-var-viz-description-color": currentColors.pureBlack,
          "--ts-var-viz-legend-hover-background": currentColors.lightGray,
          "--ts-var-axis-title-color": currentColors.pureBlack,
          "--ts-var-axis-data-label-color": currentColors.dark,
          "--ts-var-liveboard-header-font-color": currentColors.dark,
          "--ts-var-liveboard-tile-table-header-background": currentColors.pureWhite,
          "--ts-var-viz-box-shadow": `0px 20px 25px -5px ${currentColors.dark}10`,
          "--ts-var-liveboard-tile-background": currentColors.pureWhite,
          "--ts-var-liveboard-notetitle-body-font-color": currentColors.dark,
          "--ts-var-chip-color": currentColors.dark,
          // makes chips that had a meaningful background gray
          // "--ts-var-chip-background": currentColors.backgroundGray,
          "--ts-var-button--primary--hover-background": currentColors.secondaryColor,
          "--ts-var-button--secondary-color": currentColors.dark,
          "--ts-var-button--secondary-background": currentColors.backgroundGray,
          "--ts-var-button--secondary--hover-background": currentColors.softGray,
          "--ts-var-button--secondary--hover-color": currentColors.dark,
          "--ts-var-chip--active-background": currentColors.light,
          "--ts-var-chip--active-color": currentColors.dark,
          "--ts-var-chip--hover-background": currentColors.softGray,
          "--ts-var-chip--hover-color": currentColors.dark,
          "--ts-var-menu-background": currentColors.pureWhite,
          "--ts-var-menu--hover-background": currentColors.softGray,
          "--ts-var-menu--hover-color": currentColors.dark,
          "--ts-var-viz-border-radius": "1rem",
          "--ts-var-spotter-prompt-background": currentColors.lightGray,
          "--ts-var-liveboard-single-column-breakpoint": "768",
          "--ts-var-liveboard-dual-column-breakpoint": "900",
          "--ts-var-liveboard-tab-active-border-color": currentColors.primaryColor,
          "--ts-var-dialog-header-background": currentColors.pureWhite,
          "--ts-var-dialog-header-color": currentColors.dark,
          "--ts-var-dialog-body-background": currentColors.pureWhite,
          "--ts-var-dialog-footer-background": currentColors.pureWhite,
          "--ts-var-dialog-body-color": currentColors.pureBlack,
          "--ts-var-menu-color": currentColors.dark,
          "--ag-foreground-color": currentColors.dark,
          "--ag-background-color": currentColors.pureWhite,
          "--ag-odd-row-background-color": currentColors.lightGray,
          "--ag-header-background-color": currentColors.pureWhite,
          "--ts-var-answer-data-panel-background-color": currentColors.pureWhite,
          "--ts-var-answer-edit-panel-background-color": currentColors.pureWhite,
          "--ts-var-answer-view-table-chart-switcher-background": currentColors.lightGray,
          "--ts-var-answer-view-table-chart-switcher-active-background": currentColors.pureWhite,
          "--ts-var-search-navigation-button-background": currentColors.pureWhite,
          "--ts-var-search-bar-background": currentColors.pureWhite,
          "--ts-var-spotter-input-background": currentColors.pureWhite,
          "--ts-var-loader-background": currentColors.pureWhite,
          "--ts-var-application-color": currentColors.primaryColor,
          "--ts-var-change-analysis-insights-background": currentColors.pureWhite,
          "--ts-var-button--tertiary-color": currentColors.dark,
        } as CustomCssVariables,
        rules_UNSTABLE: {
          // Style the spotter prompt background
          ".spotter-prompt-module__promptBackground": {
            "background-color": currentColors.lightGray,
          },
          // the original app has a navigator which we are hiding, this margin allows the app to render below our own navigator
          ".bk-embed-powered-by-ts-ribbon.ng-scope.ng-isolate-scope": { display: "none" },
          // this margin gives breathing space in /saved-answer route
          ".answer-module__answer.flex-layout-module__horizontal.spacing-e": {
            "margin-top": "10px !important",
          },
          // hides the footer
          ".footer-module__footerContainer.layout-module__horizontal, .bk-powered-by-ts-ribbon.ng-scope.ng-isolate-scope":
            {
              display: "none !important",
            },
          // the following rules set the primary color for buttons and text around the app
          ".button-module__backgroundLight.button-module__secondary.button-module__buttonSelected, .select-module__roundedHead.select-module__selectOpen .select-module__selectedValue, .segment-control-module__segmentedControl .segment-control-module__segmentedItem.segment-control-module__selected .segment-control-module__innerText, .menu-module__item.menu-module__itemActive>.menu-module__itemInfo>.menu-module__itemText p, .typography-module__body-normal.typography-module__base.facet-sort-bar-module__placeholderFacetSelectValue.select-module__selectedValue.select-module__placeholder.typography-module__whiteSpace.typography-module__noMargin.typography-module__ellipsisText.typography-module__ellipsis, .favorite-button-module__favoriteButtonActive, .link-module__blue:hover, .top-list-filters .bk-filter-item .bk-top-menu-filters li.bk-selected, .top-list-filters .bk-filter-item .bk-top-menu-filters li:hover, .avatar-module__nameLink:hover, .bk-link-button, .menu-module__actionMenu.menu-module__sideNavVariant .menu-module__item>.menu-module__item>.menu-module__itemInfo .menu-module__itemText .menu-module__labelHighlight, .menu-module__actionMenu.menu-module__sideNavVariant .menu-module__menuGroupWrapper>.menu-module__item>.menu-module__itemInfo .menu-module__itemText .menu-module__labelHighlight, .tab-module__tabHorizontal .tab-module__headerItem.tab-module__headerItemSelected .tab-module__headerText, .link-module__blue, .pinboard-tab-module__tabNameContainer .pinboard-tab-module__tabNameEditor.pinboard-tab-module__tabHighlightOnSelected":
            {
              color: currentColors.primaryColor,
            },
          ".facet-sort-bar-module__facetSelectValue": {
            color: `${currentColors.primaryColor} !important`,
          },
          "[data-radiant-v2].icon-module__blue, .bk-link-button .svg-icon-info, .radio-module__customRadio .radio-module__customRadioCircle":
            {
              fill: currentColors.primaryColor,
            },
          ".button-module__backgroundLight.button-module__primary": {
            "background-color": currentColors.primaryColor,
            color: "#FFFFFF",
          },
          ".button-module__backgroundLight.button-module__primary:not(.button-module__disabled):hover, .rd-button-background-light.rd-button-primary.rd-button-size-m:hover":
            {
              "background-color": currentColors.secondaryColor,
              color: currentColors.pureWhite,
            },
          ".button-module__backgroundLight.button-module__primary .button-module__buttonText": {
            color: "#FFFFFF",
          },
          ".toggle-module__toggleCheckbox:checked+.toggle-module__toggleSwitchBackground .toggle-module__toggleSwitchMarker":
            {
              "background-color": currentColors.primaryColor,
              "border-color": currentColors.primaryColor,
            },
          ".bk-checkbox-container .bk-checkbox:checked+.bk-checkbox-label::before": {
            "border-color": currentColors.primaryColor,
          },
          "input[type=checkbox].checkbox-module__nativeCheckbox:indeterminate+.checkbox-module__customCheckbox, input[type=checkbox].checkbox-module__nativeCheckbox:checked+.checkbox-module__customCheckbox":
            {
              "border-color": currentColors.primaryColor,
              color: currentColors.primaryColor,
            },
          ".select-module__select .select-module__selectOpen.select-module__selectHead": {
            border: `1px solid ${currentColors.primaryColor}`,
          },
          ".top-list-filters .bk-filter-item .bk-top-menu-filters li.bk-selected::after, .tab-module__tabHorizontal .tab-module__headerItem:after":
            {
              "background-color": currentColors.primaryColor,
            },
          ".bk-link-button:hover": {
            color: currentColors.secondaryColor,
          },
          // sets the colors of KPIs
          ".homepage-carousel-card-module__card .homepage-carousel-card-module__pinboardMetricsCardHeader":
            {
              background: `linear-gradient(91.6deg,${currentColors.secondaryColor},${currentColors.primaryColor} 103.93%)`,
            },
          ".rd-button-background-light.rd-button-primary.rd-button-size-m": {
            "background-color": currentColors.primaryColor,
            color: currentColors.pureWhite,
          },
          // transforms the edit-liveboard header
          ".pinboard-edit-header-module__editActionHeader": {
            display: "flex",
            "justify-content": "end",
            height: "56px",
            "box-shadow": "none",
            "border-radius": "16px",
          },
          // hidden buttons and logos
          ".pinboard-edit-header-module__editActionHeader .pinboard-edit-header-module__headerLogo, .import-tsl-v2-header-module__tsLogo, .spotiq-box-module__analysisBegun img, .layout-module__sidePanel.layout-module__rightPanel.layout-module__expanded, .button-module__button.button-module__buttonWithIcon.button-module__secondary.button-module__sizeM.button-module__backgroundLight.button-module__both[aria-label='Create alert'][data-testid='monitor-button-action']":
            {
              display: "none",
            },
          // change colors of edit-liveboard header
          ".pinboard-edit-header-module__editActionHeader [data-radiant-v2].icon-module__text-color":
            {
              fill: currentColors.feintGray,
            },
          ".pinboard-edit-header-module__editActionHeader .button-module__backgroundColored.button-module__tertiary .button-module__buttonText":
            {
              color: currentColors.feintGray,
            },
          ".pinboard-edit-header-module__editActionHeader.pinboard-edit-header-module__editActionHeaderHeight[data-testid='pinboard-edit-header-container']":
            {
              "background-color": `${currentColors.pureWhite} !important`,
            },
          ".pinboard-edit-header-module__editActionHeader + .pinboard-header-module__pinboardHeaderContainer":
            {
              "margin-top": "80px",
            },
          // removed padding when edit-liveboard shows
          ".pinboard-header-module__headerContainerPrimaryNavHidden": {
            "padding-top": "0px",
          },
          ".button-module__buttonWrapper.toast-alert-module__closeButton .icon-module__iconStyle.icon-module__s.icon-module__text-color":
            { fill: currentColors.pureWhite },
          ".eureka-search-bar-module__searchBarWrapper, .eureka-search-bar-module__withoutSage": {
            "background-color": currentColors.dark,
          },
          ".pinboard-tab-module__tabContainer.pinboard-tab-module__tabSelected": {
            "border-bottom-color": currentColors.primaryColor,
          },
          ".import-tsl-v2-header-module__header": { padding: "0 0.7rem" },
          ".data-panel-module__header.flex-layout-module__horizontal .button-module__button.button-module__buttonWithIcon.button-module__secondary.button-module__sizeM.button-module__backgroundLight.button-module__both":
            {
              display: "none",
            },
          // stops column author from rerouting you to the author's profile
          ".ag-cell.ag-cell-not-inline-editing.ag-cell-normal-height.ag-cell-focus.ag-cell-value[aria-colindex='5'][col-id='author']":
            {
              "pointer-events": "none",
            },
          ".typography-module__body-normal.typography-module__base.avatar-module__name.avatar-module__nameLink.typography-module__whiteSpace":
            {
              cursor: "default",
              "text-decoration": "none",
            },
          ".typography-module__body-normal.typography-module__base.avatar-module__name.avatar-module__nameLink.typography-module__whiteSpace:hover":
            {
              cursor: "default",
              "text-decoration": "none",
            },
          ".ag-header-container .ag-header-cell": {
            "border-bottom": "0px !important",
          },
          // hides columns
          ".ag-header-cell[col-id='views'], .ag-cell[col-id='views'], .ag-header-cell[col-id='isVerified'], .ag-cell[col-id='isVerified']":
            {
              display: "none",
            },
          ".ag-header-row.ag-header-row-column": {
            "border-bottom": `1px solid ${currentColors.backgroundGray}`,
          },
          "div[data-testid='liveboard-users'].pinboard-header-module__avatarDiv": {
            display: "none",
          },
          ".stickers.ng-scope .svg-icon-block.svg-icon-info-block.svg-icon-block-m.rd-icon-tag-m, .stickers.ng-scope .bk-title.ng-binding, .stickers.ng-scope .sticker-selector-container, rd-button[ng-if='::$ctrl.metadataListSecondaryButtonControl'] .rd-button-wrapper":
            {
              display: "none",
            },
          ".bk-secondary-actions .bk-action-item:nth-of-type(2), .bk-secondary-actions .bk-action-item:nth-of-type(3), .bk-secondary-actions .bk-action-item:nth-of-type(4), .wizard-module__wizardHeader":
            {
              display: "none",
            },
          ".ReactModal__Content.ReactModal__Content--after-open.modal-module__modal.modal-module__fullscreen":
            {
              "margin-top": "85px",
            },
          // hide request access for liveboards
          ".request-access-module__requestAccessBtn": {
            display: "none",
          },
          // hide AI highlights
          ".pinboard-header-module__pinboardActionContainer #highlights": {
            display: "none",
          },
          // hide SpotIQ default preferences
          ".spotiq-actionbar-module__defaultActionBar .button-module__button.button-module__buttonWithIcon.button-module__buttonWithText.button-module__secondary.button-module__sizeM.button-module__backgroundLight.button-module__both":
            {
              display: "none",
            },
          // hide Modify schedule from SpotIQ
          "#actionmenu_rd-val-default #schedule-job": {
            display: "none",
          },
          // hide SpotIQ dropdown options
          ".popover-module__positionable.popover-module__in #Share, .popover-module__positionable.popover-module__in #schedule_jobs, .popover-module__positionable.popover-module__in #analysis_details":
            {
              display: "none",
            },
          // hide action (lightning) button of search data and answers
          "[data-testid=action-selector-nav-btn]": {
            display: "none",
          },
          // hide KPI actions
          ".popover-module__positionable.popover-module__in #embedDocument, .popover-module__positionable.popover-module__in #manageMonitor":
            {
              display: "none",
            },
          // hides actions that we couldn't hide through TS Actions
          "#actionmenu_rd-val-default #tml": {
            display: "none !important",
          },
          "#actionmenu_rd-val-default .divider-module__includeSectionBorder:after": {
            display: "none !important",
          },
          // tightens the space between navbar and container in SpotIQ
          "[data-testid=spotiq-list-container] .metadata-list-module__container": {
            "padding-top": "0px",
          },
          ".button-module__buttonWrapper.pinboard-header-module__shareButton [data-testid=share-button]":
            {
              "background-color": currentColors.primaryColor,
              color: currentColors.pureWhite,
            },
          ".button-module__buttonWrapper.pinboard-header-module__shareButton:hover [data-testid=share-button]":
            {
              "background-color": currentColors.secondaryColor,
            },
          // hides and stylizes lite layout scroll
          ".pinboard-module__pinboardPage::-webkit-scrollbar": {
            width: "5px",
            "background-color": "transparent",
          },
          ".pinboard-module__pinboardPage::-webkit-scrollbar-track": {
            background: "transparent",
            "border-radius": "10px",
          },
          ".pinboard-module__pinboardPage::-webkit-scrollbar-thumb": {
            background: "transparent",
            "border-radius": "10px",
            transition: "background-color 0.3s ease",
          },
          ".pinboard-module__pinboardPage:hover::-webkit-scrollbar-track": {
            background: "transparent",
          },
          ".pinboard-module__pinboardPage:hover::-webkit-scrollbar-thumb": {
            background: currentColors.gray,
          },
          ".pinboard-module__pinboardPage:hover::-webkit-scrollbar-thumb:hover": {
            background: currentColors.feintGray,
          },
          // hides view availability box
          ".rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper #isPublic": {
            display: "none",
          },
          // hides the header cell for the "Anyone can view" column
          "[data-testid='manage-personalised-views'] .ag-root-wrapper .ag-header-cell[aria-colindex='3']":
            {
              display: "none !important",
            },
          // hides the cell content for the "Anyone can view" column
          "[data-testid='manage-personalised-views'] .ag-root-wrapper .ag-cell[aria-colindex='3']":
            {
              display: "none !important",
            },
          // hides data source in visualization edit
          ".viz-context-answer-module__wrapper .viz-context-answer-module__bkContextDialogContainer .viz-context-answer-module__content .sage-search-bar-with-error-bar-module__sageDataSourcePreviewSection":
            {
              "min-width": "0px",
            },
          ".viz-context-answer-module__wrapper .viz-context-answer-module__bkContextDialogContainer .viz-context-answer-module__content .sage-data-source-preview-module__sageDataSourcePreviewContainer.layout-module__horizontal":
            {
              display: "none !important",
            },
          // hides data source in search data
          ".sage-search-bar-with-error-bar-module__sageDataSourcePreviewSection": {
            "min-width": "0px",
          },
          ".sage-data-source-preview-module__sageDataSourcePreviewContainer.layout-module__horizontal":
            {
              display: "none !important",
            },
          // remove outside spotter scrollbar
          ".authenticated-app-view-module__pageContent.authenticated-app-view-module__force-overflow-auto":
            { overflow: "visible !important" },
          // changes search backdrop color
          ".answer-module__searchCurtain": {
            "background-color": currentColors.lightGray,
          },
          // hiding search data button
          ".muted-alert-module__actionButtons.muted-alert-module__m": {
            display: "none",
          },
          // changing edit liveboard header size
          ".pinboard-edit-header-module__editActionHeader.pinboard-edit-header-module__editActionHeaderHeight":
            {
              right: "0",
              left: "0",
            },
          ".pinboard-header-module__pinboardHeaderContainer.pinboard-header-module__embedPinboardHeader.layout-module__vertical":
            {
              "margin-left": "1.8rem",
              "margin-right": "1.8rem",
            },
          // visualization edit header fixes
          ".viz-context-answer-module__popupHeader.flex-layout-module__horizontal": {
            "border-radius": "10px",
          },
          ".viz-context-answer-module__bkContent .viz-context-answer-module__content .answer-module__answer":
            {
              padding: "1.7rem",
            },
          ".viz-context-answer-module__bkContextDialogContainer .viz-context-answer-module__content .viz-context-answer-module__popupHeader":
            {
              "margin-left": "1.7rem",
              "margin-right": "1.7rem",
            },
          ".viz-context-answer-module__bkContextDialog": {
            "background-color": currentColors.lightGray,
          },
          // Scrollbar changes to fit our own
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer::-webkit-scrollbar, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical::-webkit-scrollbar, [data-testid='schedule-modal-content']::-webkit-scrollbar, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical::-webkit-scrollbar, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding::-webkit-scrollbar, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content::-webkit-scrollbar":
            {
              width: "5px",
              "background-color": "transparent",
            },
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer::-webkit-scrollbar-track, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical::-webkit-scrollbar-track, [data-testid='schedule-modal-content']::-webkit-scrollbar-track, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical::-webkit-scrollbar-track, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding::-webkit-scrollbar-track, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content::-webkit-scrollbar-track":
            {
              background: "transparent",
              "border-radius": "10px",
            },
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer::-webkit-scrollbar-thumb, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical::-webkit-scrollbar-thumb, [data-testid='schedule-modal-content']::-webkit-scrollbar-thumb, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical::-webkit-scrollbar-thumb, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding::-webkit-scrollbar-thumb, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content::-webkit-scrollbar-thumb":
            {
              background: "transparent",
              "border-radius": "10px",
              transition: "background-color 0.3s ease",
            },
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer:hover::-webkit-scrollbar-track, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical:hover::-webkit-scrollbar-track, [data-testid='schedule-modal-content']:hover::-webkit-scrollbar-track, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical:hover::-webkit-scrollbar-track, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding:hover::-webkit-scrollbar-track, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content:hover::-webkit-scrollbar-track":
            {
              background: "transparent",
            },
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer:hover::-webkit-scrollbar-thumb, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical:hover::-webkit-scrollbar-thumb, [data-testid='schedule-modal-content']:hover::-webkit-scrollbar-thumb, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical:hover::-webkit-scrollbar-thumb, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding:hover::-webkit-scrollbar-thumb, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content:hover::-webkit-scrollbar-thumb":
            {
              background: currentColors.gray,
            },
          ".data-sources-v2-module__mainAccordionContainer.data-sources-v2-module__marginForOuterContainer:hover::-webkit-scrollbar-thumb:hover, .authenticated-app-view-module__blink.authenticated-app-view-module__blinkInEmbedWithFooter.flex-layout-module__vertical:hover::-webkit-scrollbar-thumb:hover, [data-testid='schedule-modal-content']:hover::-webkit-scrollbar-thumb:hover, .universal-share-dialog-share-list-module__shareItemListContainer.flex-layout-module__vertical:hover::-webkit-scrollbar-thumb:hover, .modal-module__content.modal-module__autoScrollContent.modal-module__noPadding:hover::-webkit-scrollbar-thumb:hover, .rdLoaderGlobalWrap.rdHideLoader.modal-module__loadingWrapper .modal-module__content:hover::-webkit-scrollbar-thumb:hover":
            {
              background: currentColors.feintGray,
            },
          // moved sage search to match rest of app
          ".sage-search-bar-with-error-bar-module__sageSearchBarWithErrorBarContainerWithDataPanelV2":
            {
              "margin-left": "1.1rem",
              "margin-right": "0",
              "margin-bottom": "0.5rem !important",
              "margin-top": "1rem",
            },
          ".answer-module__answer.answer-module__answerWithDataPanelV2.flex-layout-module__horizontal":
            {
              "margin-left": "1.1rem",
              padding: "0",
            },
          // fixing bar liveboards width toggling
          "#liveboard-grid.pinboard-content-module__gridContainer": {
            "padding-right": "0.1rem",
          },
          // Liveboard changes to match light and dark mode
          ".select-module__selectBody, .explainer-card-module__card": {
            "background-color": currentColors.pureWhite,
            color: currentColors.pureBlack,
          },
          ".dx-widget, .tab-module__tabHorizontal .tab-module__headerItem .tab-module__headerText":
            {
              color: currentColors.dark,
            },
          ".pinboard-header-module__headerDescription.content-editable-module__contentEditable.content-editable-module__noMargin":
            {
              color: currentColors.pureBlack,
            },
          // Fix Liveboard Modals and Menus for dark mode
          ".modal-module__modal, .menu-module__actionMenu, .pinboard-schedule-modal-module__scheduleModalV2 .pinboard-schedule-modal-module__modalFooter, .pinboard-schedule-modal-module__scheduleModalV2 .pinboard-schedule-modal-module__scheduleWrapper .pinboard-schedule-modal-module__sideMenuWrapper, .pinboard-schedule-modal-module__scheduleModalV2 .pinboard-schedule-modal-module__filterWrapper, .autosuggestion-container-module__autoSuggestionContainer":
            {
              "background-color": currentColors.pureWhite + " !important",
            },
          ".chip-base-module__chip.chip-base-module__input.chip-base-module__default:active, .chip-base-module__chip.chip-base-module__input.chip-base-module__default":
            {
              "background-color": currentColors.backgroundGray + " !important",
            },
          ".dx-word-wrap, .dx-pivotgrid-area, .dx-pivotgrid-area-data, .dx-area-data-cell, .dx-pivotgrid-horizontal-headers, .dx-pivotgrid-vertical-headers, .dx-pivotgrid-background, .popover-module__positionable":
            {
              "background-color": currentColors.pureWhite + " !important",
              color: currentColors.dark,
            },
          ".list-item-module__itemContainer.list-item-module__isSelected": {
            "background-color": currentColors.pureWhite,
          },
          ".pivot-table .dx-pivotgrid .dx-pivotgrid-area-data tbody td, .pivot-table .dx-pivotgrid .dx-pivotgrid-area td":
            {
              color: currentColors.dark,
            },
          ".pinboard-schedule-modal-module__scheduleModalV2 .pinboard-schedule-modal-module__scheduleWrapper .pinboard-schedule-modal-module__sideMenuWrapper [class*=modalSideNavVariant]:hover":
            {
              "background-color": currentColors.softGray + " !important",
              color: currentColors.pureWhite + " !important",
            },
          ".banner-alert-module__container.banner-alert-module__info, .bk-outline .ag-theme-alpine .ag-row:hover, .table-module__cellSelected":
            {
              "background-color": currentColors.lightGray,
            },
          ".popover-module__positionable ": {
            "box-shadow":
              theme === "light"
                ? "0 12px 24px -4px #1c233014,0 2px 4px #1c23300a"
                : "0 12px 24px -4px #f1f5f914,0 2px 4px #f1f5f90a",
          },
          // answer tag colors dark mode
          ".list-item-v2-module__measure, .sage-search-box-module__measureToken": {
            "background-color": currentColors.measureColor,
            "border-bottom-color": currentColors.measureColor,
          },
          ".sage-search-box-module__measureToken:hover": {
            "background-color": currentColors.measureColorHover,
            "border-bottom-color": currentColors.measureColorHover,
          },
          ".list-item-v2-module__attribute, .sage-search-box-module__attributeToken": {
            "background-color": currentColors.attributeColor,
            "border-bottom-color": currentColors.attributeColor,
          },
          ".sage-search-box-module__attributeToken:hover": {
            "background-color": currentColors.attributeColorHover,
            "border-bottom-color": currentColors.attributeColorHover,
          },
          ".list-item-v2-module__date, .sage-search-box-module__dateBucketToken": {
            "background-color": currentColors.dateColor,
            "border-bottom-color": currentColors.dateColor,
          },
          ".sage-search-box-module__dateBucketToken:hover": {
            "background-color": currentColors.dateColorHover,
            "border-bottom-color": currentColors.dateColorHover,
          },
          ".sage-search-box-module__unrecognizedToken, .banner-alert-module__container.banner-alert-module__error":
            {
              "background-color": currentColors.unrecognizedColor,
              "border-bottom-color": currentColors.unrecognizedColor,
            },
          // ".sage-search-box-module__pendingToken, .sage-search-box-module__otherToken":
          // {
          //   "background-color": currentColors.light,
          //   "border-bottom-color": currentColors.light,
          //   color: currentColors.lightGray + " !important",
          // },
          // ".sage-search-box-module__otherToken:hover,.sage-search-box-module__pendingToken:hover":
          // {
          //   "background-color": currentColors.light,
          //   "border-bottom-color": currentColors.light,
          // },
          ".tokenizer-module__tokenizerContainer .tokenizer-module__token.tokenizer-module__bkEditedNode":
            {
              color: "black !important",
            },
          ".sage-search-box-module__pendingToken, .sage-search-box-module__pendingToken:hover": {
            "border-color": currentColors.primaryColor,
          },
          // answer background sidebar dark mode
          ".data-sources-v2-module__dataSourcesV2Container, .data-sources-v2-module__dataSourcesV2Container, .data-sources-v2-module__viewSwitcherTabs":
            {
              "background-color": currentColors.lightGray,
            },
          ".data-panel-v2-module__columnOverviewCard": {
            "background-color": currentColors.pureWhite,
          },
          "[data-radiant-v2].icon-module__darkgray": {
            fill: currentColors.primaryColor,
          },
          "[data-radiant-v2].icon-module__stroke-blue": {
            stroke: currentColors.secondaryColor + " !important",
          },
          ".accordion-module__accordionItemHeaderWrapper:hover": {
            "background-color": currentColors.lightGray,
          },
          ".sage-search-bar-with-error-bar-module__sageDataSourceCollapseIconContainer, .sage-completions-dropdown-module__completionsDropdown":
            {
              "background-color": currentColors.pureWhite,
            },
          ".sage-completions-dropdown-module__completionsDropdownItem:hover": {
            "background-color": currentColors.lightGray,
          },
          // Override Highcharts dark mode
          ".highcharts-data-label text": {
            "text-shadow": `${currentColors.pureWhite} 0px 0px 2px !important`,
          },
          ".highcharts-text-outline": {
            fill: `${currentColors.feintWhite} !important`,
            stroke: `${currentColors.pureWhite} !important`,
          },
          // Table fixes dark mode
          ".pivot-table": {
            "border-top": `1px solid ${currentColors.backgroundGray}`,
            "border-bottom": `1px solid ${currentColors.backgroundGray}`,
            "border-left": `1px solid ${currentColors.backgroundGray}`,
            "border-right": `1px solid ${currentColors.backgroundGray}`,
          },
          ".dx-pivotgrid td.dx-area-description-cell.dx-pivotgrid-background": {
            "border-bottom": `1px solid ${currentColors.backgroundGray} !important`,
          },
          ".bk-row .ag-theme-alpine .ag-row:hover": {
            "background-color": currentColors.lightGray,
          },
          "[data-testid='kpi_comparision_diff']": {
            "background-color": "transparent",
          },
          ".pivot-table .dx-pivotgrid.dx-row-lines .dx-pivotgrid-area td": {
            "border-top": `0.1rem solid ${currentColors.backgroundGray}`,
          },
          ".dx-pivotgrid .dx-pivotgrid-horizontal-headers td": {
            border: `1px solid ${currentColors.backgroundGray}`,
          },
          ".pivot-table .dx-pivotgrid .dx-column-header .dx-pivotgrid-fields-area": {
            "border-bottom": `1px solid ${currentColors.backgroundGray} !important`,
          },
          ".pivot-table .dx-pivotgrid .dx-area-description-cell": {
            "border-right": `1px solid ${currentColors.backgroundGray}`,
          },
          ".dx-pivotgrid .dx-area-row-cell": {
            "border-right": `1px solid ${currentColors.backgroundGray}`,
          },
          ".dx-pivotgrid .dx-pivotgrid-area-data tbody td": {
            "border-left": `1px solid ${currentColors.backgroundGray}`,
          },
          // Present pagination
          ".slide-show-module__paginationContainer, .slide-show-module__paginationContainer:hover":
            {
              "background-color": currentColors.lightGray,
              color: currentColors.dark,
            },
          // Loading bg dark mode transparent
          ".rdLoadingIndicator, .rdLoadingIndicatorLowOpacity, .rdLoaderGlobalWrap, .rdHideLoader, .modal-module__loadingWrapper, .authenticated-app-view-module__blink, .embed-module__tsEmbedContainer":
            {
              "background-color": "transparent !important",
              background: "transparent !important",
            },
          ".embed-module__tsEmbedContainer .rdLoadingIndicator, .rdLoadingIndicatorLowOpacity": {
            "background-color": `${currentColors.pureWhite} !important`,
            background: `${currentColors.pureWhite} !important`,
          },
          // Dark Mode border colors
          ".pinboard-header-module__pinboardHeaderContainer": {
            "border-bottom": `1px solid ${currentColors.backgroundGray}`,
          },
          ".pinboard-tab-panel-module__tabPanel": {
            "border-top": `1px solid ${currentColors.backgroundGray}`,
          },
          ".answer-title-module__titleTextEdit:focus": {
            outline: `1px solid ${currentColors.primaryColor}`,
          },
          // Font colors Dark Mode
          ".typography-module__accent, .pinboard-tab-module__tabContainer:hover": {
            color: currentColors.primaryColor,
          },
          // Search data dark mode
          ".sage-completions-dropdown-module__footerHelpContainer, .sage-completions-dropdown-module__completion:hover, .sage-completions-dropdown-module__moreContainer:hover, .sage-completions-dropdown-module__highlight":
            {
              background: `${currentColors.lightGray} !important`,
            },
          ".monaco-editor, .monaco-editor-background, .monaco-editor .inputarea.ime-input, .monaco-editor .margin":
            {
              "background-color": currentColors.pureWhite,
              color: currentColors.dark,
            },
          ".code-editor-module__currentlyEditingtToken": {
            color: currentColors.dark,
          },
          ".monaco-editor .line-numbers.active-line-number, .mtk1, .form-control-module__label": {
            color: currentColors.pureBlack,
          },
          ".monaco-editor .line-numbers, .formula-editor-module__assistantAccordionListItemHighlight, .form-control-module__hasFocus .form-control-module__label":
            {
              color: currentColors.primaryColor,
            },
          ".formula-editor-module__valueToken, muze-layer-text-grid-container": {
            color: currentColors.dark + " !important",
          },
          ".form-builder-module__sectionHeader": {
            color: currentColors.dark,
          },
          ".tokenizer-module__tokenizerContainer:not(.tokenizer-module__disableHoverEffects):not(.tokenizer-module__nonInteractive) .tokenizer-module__separator:not(.tokenizer-module__bkEditedNode):hover":
            {
              "background-color": currentColors.lightGray,
            },
          ".list-item-v2-module__highlighted": {
            "background-color": currentColors.lightGray,
          },
          // Conditional Formatting dark mode
          ".left-content-pane-module__leftPaneWrapper, .left-content-pane-module__columnsListEmptySpace, .left-content-pane-module__leftPaneLabel, .left-content-pane-module__columnTile":
            {
              "background-color": currentColors.lightGray,
            },
          ".conditional-rule-editor-module__body, .left-content-pane-module__columnTile:hover": {
            background: currentColors.lightGray,
          },
          ".left-content-pane-module__columnTile.left-content-pane-module__activeColumn, .left-content-pane-module__columnsWithConditionToggleWrapper, .conditional-formatting-editor-module__rightPaneWrapper, .colorpicker-module__content":
            {
              background: currentColors.pureWhite + " !important",
            },
          ".left-content-pane-module__leftPaneLabel, .left-content-pane-module__leftPaneLabel, .left-content-pane-module__leftPaneLabel, .left-content-pane-module__leftPaneLabel":
            {
              color: currentColors.dark,
            },
          // Dialog dark mode
          ".modal-module__header": {
            "border-bottom": `1px solid ${currentColors.pureBlack}`,
          },
          // Query Vizualizer dark mode
          "div.query-visualizer-module__fullscreenModal, .graph-legend-module__chartLegend, .graph-legend-module__chartLegend h3":
            {
              "background-color": currentColors.pureWhite,
              color: currentColors.dark,
            },
          "div.query-visualizer-module__fullscreenModal div.query-visualizer-module__modalHeader, .graph-legend-module__chartLegend .graph-legend-module__closeLegend":
            {
              color: currentColors.dark,
            },
          "[data-radiant-v2].icon-module__black": {
            fill: currentColors.pureBlack,
          },
          // SQL Query dark mode
          ".query-details-module__sqlQuery": {
            "background-color": currentColors.pureWhite,
            color: currentColors.dark,
          },
          // Spotter dark mode
          ".chat-input-module__sendIconContainer": {
            background: currentColors.primaryColor,
          },
          // Change analysis dark mode
          ".kpi-module__backgroundColorComparison:hover": {
            "background-color": currentColors.lightGray,
          },
          ".change-analysis-modal-module__historyControllerWrapper button": {
            "background-color": currentColors.lightGray + " !important",
            color: currentColors.dark,
          },
          ".change-analysis-modal-module__attributeList": {
            "background-color": currentColors.pureWhite,
            "border-right": `1px solid ${currentColors.backgroundGray}`,
            color: currentColors.dark,
          },
          ".change-analysis-modal-module__selectedAttribute": {
            "background-color": currentColors.lightGray,
            color: currentColors.dark,
          },
          ".change-analysis-modal-module__customizeAttributesBtn": {
            background: currentColors.pureWhite,
            "border-top": `1px solid ${currentColors.backgroundGray}`,
            "border-right": `1px solid ${currentColors.backgroundGray}`,
          },
          ".change-analysis-modal-module__suggestedAttributesContainer .change-analysis-modal-module__suggestedAttribute":
            {
              color: currentColors.primaryColor,
            },
          ".change-analysis-modal-module__badgeContainer": {
            "border-right": `1px solid ${currentColors.backgroundGray}`,
          },
          ".change-analysis-modal-module__changeAnalysisModalContent": {
            border: `1px solid ${currentColors.backgroundGray}`,
          },
          ".change-analysis-modal-module__badgeBoxContainer": {
            "border-bottom": `1px solid ${currentColors.backgroundGray}`,
          },
          ".liveboard-styling-edit-header-module__addButton > .button-module__backgroundColored.button-module__tertiary .button-module__buttonText":
            {
              color: currentColors.feintWhite,
            },
          ".liveboard-styling-edit-header-module__logoWrapper": {
            width: "0px !important",
            height: "0px !important",
          },
        },
      },
    },
  };
};

export const customizations = getCustomizations("light");
